import { useCallback, useEffect, useState } from "react";
import { Flex } from "@mantine/core";
import { useNavigate } from "react-router";
import styled from "styled-components";
import dayjs from "dayjs";
import { BellAlertIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { BoxTitle, Container } from "./StyledComponents";
import { theme } from "../../theme";
import { getCorrectionRequests } from "../../api/reportsApi";

/**
 * BEKLEYEN ÇALIŞAN BİLDİRİMLERİ — ana sayfadaki uyarı yüzeyi (plan 2.50 · 2.59).
 *
 * ⚠ NEDEN VAR: kuyruk "İK'nın iş kuyruğu" diye tasarlandı ama görünürlüğü
 * yoktu — yalnızca `Raporlar → Çalışan Bildirimleri` altında, sidebar'ın
 * üçüncü alt maddesinde duruyordu. İK oraya girmedikçe bekleyen bir bildirimin
 * varlığından haberdar olmuyordu; çalışan da bildirimini gönderip cevapsız
 * kalıyordu.
 *
 * ⚠ KARAR BURADA VERİLMEZ. "Kabul et / Reddet" bilerek yok: karar ekranı iki
 * yerde yaşarsa ikisi de bakım ister ve ana sayfa bir iş masasına döner. Bu
 * kart yalnızca "bakılacak bir şey var mı" sorusuna cevap verir, karar için
 * kuyruğa gönderir.
 *
 * ⚠ DASHBOARD FİLTRELERİNİ (departman/rol) BİLEREK DİNLEMEZ: uç yalnızca
 * `status` alıyor. Filtreye tepki veriyormuş gibi görünüp vermemektense hiç
 * bağlanmaması dürüst — sayı her zaman ŞİRKETİN tamamı için doğru.
 */

/** Ana sayfada gösterilen en yeni bildirim sayısı; gerisi kuyrukta. */
const GOSTERILEN = 4;

const PendingNotices = ({ flex }: { flex: any }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data }: any = await getCorrectionRequests("pending");
      setRows(data?.data ?? []);
    } catch (error) {
      // Sessiz geç: bu kart bir uyarı yüzeyi, sayfanın ön koşulu değil.
      // Kuyruk sayfası hatayı zaten bildirimle gösteriyor.
      console.error("Bekleyen bildirimler alınamadı:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const kuyrugaGit = () => navigate("/reports/correction-requests");

  return (
    <Container $flex={flex}>
      <HeaderRow>
        <Flex align="center" gap={10}>
          <HeaderIcon $bos={rows.length === 0}>
            <BellAlertIcon width={18} />
          </HeaderIcon>
          <BoxTitle>Bekleyen Bildirimler</BoxTitle>
          {!loading && rows.length > 0 && <CountPill>{rows.length}</CountPill>}
        </Flex>
        <SeeAll onClick={kuyrugaGit}>
          Tümünü gör
          <ChevronRightIcon width={16} />
        </SeeAll>
      </HeaderRow>

      {loading ? (
        <Empty>Yükleniyor…</Empty>
      ) : rows.length === 0 ? (
        <Empty>Bekleyen bildirim yok.</Empty>
      ) : (
        <NoticeList>
          {rows.slice(0, GOSTERILEN).map((row: any) => (
            <NoticeRow key={row.id} onClick={kuyrugaGit}>
              <Name>{row.employeeName}</Name>
              <Meta>
                {dayjs(row.workday).format("DD.MM.YYYY")} · çıkış okutulmamış
              </Meta>
              <ClaimedTime>{dayjs(row.claimedExitAt).format("HH:mm")}</ClaimedTime>
            </NoticeRow>
          ))}
          {rows.length > GOSTERILEN && (
            <MoreLine onClick={kuyrugaGit}>
              +{rows.length - GOSTERILEN} bildirim daha
            </MoreLine>
          )}
        </NoticeList>
      )}
    </Container>
  );
};

export default PendingNotices;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const HeaderIcon = styled.div<{ $bos: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  /* Bekleyen iş varken turuncu, yokken nötr: renk tek başına bilgi taşısın. */
  background: ${(p) =>
    p.$bos ? `${theme.colors.greyscale.gray60}15` : `${theme.colors.alert.orange}15`};
  color: ${(p) =>
    p.$bos ? theme.colors.greyscale.gray60 : theme.colors.alert.orange};
  border: 1px solid
    ${(p) =>
      p.$bos ? `${theme.colors.greyscale.gray60}20` : `${theme.colors.alert.orange}30`};
`;

const CountPill = styled.div`
  min-width: 26px;
  height: 24px;
  padding: 0 8px;
  border-radius: 50px;
  background: ${theme.colors.alert.orange};
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  /* Türkçe rakam/harf tepesi kırpılmasın diye satır yüksekliği kutuya eşit. */
  line-height: 24px;
  text-align: center;
`;

const SeeAll = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.brand.normal};
  flex-shrink: 0;
  transition: background 0.2s ease;
  &:hover {
    background: ${theme.colors.brand.normal}12;
  }
`;

const NoticeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NoticeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover {
    background: ${theme.colors.greyscale.gray00};
  }
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.greyscale.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Uzun ad diğer iki kolonu ezmesin; kalan yeri bu alıyor. */
  flex: 1;
  min-width: 0;
`;

const Meta = styled.div`
  font-size: 12px;
  color: ${theme.colors.greyscale.gray80};
  white-space: nowrap;
  flex-shrink: 0;
`;

const ClaimedTime = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${theme.colors.greyscale.black};
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  min-width: 44px;
  text-align: right;
`;

const MoreLine = styled.button`
  border: none;
  background: transparent;
  text-align: left;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.brand.normal};
  cursor: pointer;
`;

const Empty = styled.div`
  font-size: 14px;
  color: ${theme.colors.greyscale.gray60};
  padding: 8px 10px;
`;
